import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IconTrash, IconPencil } from '@tabler/icons-react';

import {
  Modal,
  Button,
  Field,
  IconButton,
  EmptyState,
  useToast,
} from '../../design/components';
import {
  createRack,
  renameRack,
  deleteRack,
} from '@recipe-rack/shared';
import type { Rack } from '@recipe-rack/shared';

import { db } from '../Recipe/Firebase/InitialiseFirebase';

export interface ManageRacksProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  racks: Rack[];
}

const ManageRacks: React.FC<ManageRacksProps> = ({
  open,
  onClose,
  userId,
  racks,
}) => {
  const toast = useToast();
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await createRack(db, userId, {
        rackId: uuidv4(),
        name,
        createdAt: Date.now(),
      });
      setNewName('');
      toast.show('Rack added', 'success');
    } catch (err) {
      console.error('Failed to create rack', err);
      toast.show('Could not add rack', 'error');
    } finally {
      setCreating(false);
    }
  };

  const startRename = (rack: Rack) => {
    setEditingId(rack.rackId);
    setEditingName(rack.name);
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveRename = async (rackId: string) => {
    const name = editingName.trim();
    if (!name) {
      cancelRename();
      return;
    }
    try {
      await renameRack(db, userId, rackId, name);
      toast.show('Rack renamed', 'success');
    } catch (err) {
      console.error('Failed to rename rack', err);
      toast.show('Could not rename rack', 'error');
    } finally {
      cancelRename();
    }
  };

  const handleDelete = async (rack: Rack) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Delete "${rack.name}"? Recipes inside it stay in your library.`
    );
    if (!ok) return;
    try {
      await deleteRack(db, userId, rack.rackId);
      toast.show('Rack deleted', 'success');
    } catch (err) {
      console.error('Failed to delete rack', err);
      toast.show('Could not delete rack', 'error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manage racks"
      size="md"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="rr-racks-create">
        <Field
          label="New rack"
          placeholder="e.g. Sunday Lunch"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleCreate();
            }
          }}
        />
        <Button
          variant="primary"
          onClick={handleCreate}
          loading={creating}
          disabled={!newName.trim()}
        >
          Add
        </Button>
      </div>

      {racks.length === 0 ? (
        <EmptyState
          title="No racks yet"
          description="Create a rack above to group recipes."
        />
      ) : (
        <>
          <h4 className="rr-recipe-eyebrow" style={{ margin: '0 0 8px' }}>
            Your racks
          </h4>
          <ul className="rr-racks-list">
            {racks.map((rack) => {
              const isEditing = editingId === rack.rackId;
              return (
                <li key={rack.rackId}>
                  {isEditing ? (
                    <input
                      autoFocus
                      className="rr-field"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => void saveRename(rack.rackId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void saveRename(rack.rackId);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelRename();
                        }
                      }}
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="rr-racks-list-name"
                      onClick={() => startRename(rack)}
                      title="Click to rename"
                    >
                      {rack.name}
                    </button>
                  )}
                  {!isEditing && (
                    <IconButton
                      icon={<IconPencil size={16} />}
                      aria-label={`Rename ${rack.name}`}
                      size="sm"
                      onClick={() => startRename(rack)}
                    />
                  )}
                  <IconButton
                    icon={<IconTrash size={16} />}
                    aria-label={`Delete ${rack.name}`}
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(rack)}
                  />
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Modal>
  );
};

export default ManageRacks;
